import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, MessageCircle, Check, X, Phone } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { Switch } from '@/v5/components/ui/switch';
import { useToast } from '@/hooks/useToast';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { cn } from '@/lib/utils';
import { apiClient } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import { SmsEditor, SmsPhoneMockup } from '@/components/sms-editor';
import {
  getSmsSegments, calcCost, MMS_MAX_FILES,
  ensureSmsOptOut, hasSmsMessageContent, hasSmsOptOut, SMS_OPT_OUT_TOKEN,
} from '@/components/sms-editor/sms-editor-constants';

interface ActivationReviewItem {
  label: string;
  ok: boolean;
  value?: string | null;
  optional?: boolean;
  detail?: string;
}

function segmentRating(segs: number) {
  if (segs === 0) return { label: 'No content', cls: 'fail', detail: 'Your message is empty.' };
  if (segs <= 2) return { label: 'Excellent', cls: 'pass', detail: 'Very concise. Low cost per message.' };
  if (segs <= 4) return { label: 'Good', cls: 'pass', detail: 'Reasonable length. Standard cost.' };
  if (segs <= 8) return { label: 'Moderate', cls: 'info', detail: 'Message is getting long. Each segment increases cost.' };
  if (segs <= 10) return { label: 'Long', cls: 'info', detail: 'Consider shortening. Carriers may split very long messages.' };
  return { label: 'Very long', cls: 'fail', detail: 'Exceeds recommended limits. Some carriers may reject messages over 10 segments.' };
}

/** The templates list opens on email; ?tab=sms brings people back to the SMS tab. */
const SMS_TEMPLATES_LIST = '/marketing/templates?tab=sms';

export default function SmsTemplateBuilderPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const isNew = !idParam || idParam === 'new';
  const numericId = isNew ? null : Number(idParam);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [folder, setFolder] = useState('general');
  const [active, setActive] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [templateId, setTemplateId] = useState<number | null>(numericId);

  const [showDelete, setShowDelete] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhoneDigits, setTestPhoneDigits] = useState('');
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activationReview, setActivationReview] = useState<ActivationReviewItem[]>([]);
  const [activationHasErrors, setActivationHasErrors] = useState(false);

  // ── Queries ──
  const { data: template, isLoading } = useQuery({
    queryKey: ['marketing', 'sms-template', numericId],
    queryFn: async () => {
      const res = await apiClient.get(`/marketing/sms-templates/${numericId}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!numericId,
  });

  const { data: foldersData } = useQuery({
    queryKey: ['marketing', 'sms-template-folders'],
    queryFn: () => marketingSvc.getSmsTemplateFolders(),
  });

  const folderOptions = useMemo(() => {
    const opts = [{ value: 'general', label: 'General' }];
    if (Array.isArray(foldersData)) {
      foldersData.forEach(({ name }) => {
        if (name.toLowerCase() !== 'general') {
          opts.push({ value: name, label: name.charAt(0).toUpperCase() + name.slice(1) });
        }
      });
    }
    return opts;
  }, [foldersData]);

  const baselineRef = useRef<Record<string, unknown>>({});

  useEffect(() => {
    if (template && !initialized) {
      const resolvedName = template.name ?? '';
      const resolvedDesc = template.description ?? '';
      const resolvedBody = template.messageBody ?? template.body ?? '';
      const resolvedMedia = template.mediaUrls ?? template.media_urls ?? [];
      const resolvedFolder = template.folder ?? 'general';
      const resolvedActive = template.active ?? false;

      setName(resolvedName);
      setDescription(resolvedDesc);
      setMessageBody(resolvedBody);
      setMediaUrls(resolvedMedia);
      setFolder(resolvedFolder);
      setActive(resolvedActive);
      setTemplateId(template.smsTemplateId ?? template.id ?? numericId);

      baselineRef.current = {
        name: resolvedName, description: resolvedDesc, messageBody: resolvedBody,
        mediaUrls: JSON.stringify(resolvedMedia), folder: resolvedFolder, active: resolvedActive,
      };
      setInitialized(true);
    }
  }, [template, initialized, numericId]);

  // ── Unsaved changes ──
  const hasUnsavedChanges = useMemo(() => {
    if (isNew || !template || !initialized) return false;
    const b = baselineRef.current;
    if (name !== b.name) return true;
    if (description !== b.description) return true;
    if (messageBody !== b.messageBody) return true;
    if (JSON.stringify(mediaUrls) !== b.mediaUrls) return true;
    if (folder !== b.folder) return true;
    if (active !== b.active) return true;
    return false;
  }, [isNew, template, initialized, name, description, messageBody, mediaUrls, folder, active]);

  useUnsavedChangesGuard(hasUnsavedChanges);

  // ── Derived ──
  const hasMedia = mediaUrls.length > 0;
  const cost = calcCost(1, messageBody, hasMedia);

  // ── Mutations ──
  const saveMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => {
      if (templateId) {
        return apiClient.put(`/marketing/sms-templates/${templateId}`, data).then((r) => r.data?.data ?? r.data);
      }
      return apiClient.post('/marketing/sms-templates', data).then((r) => r.data?.data ?? r.data);
    },
    onSuccess: (data) => {
      if (!templateId && data?.smsTemplateId) setTemplateId(data.smsTemplateId);
      baselineRef.current = {
        name: name.trim(), description, messageBody,
        mediaUrls: JSON.stringify(mediaUrls), folder, active,
      };
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-templates'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-template', numericId] });
      toast.success('Template saved');
    },
    onError: () => toast.error('Failed to save template'),
  });

  const deleteMut = useMutation({
    mutationFn: () => apiClient.delete(`/marketing/sms-templates/${templateId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-templates'] });
      toast.success('Template deleted');
      navigate(SMS_TEMPLATES_LIST);
    },
    onError: () => toast.error('Failed to delete template'),
  });

  // Sends the on-screen draft, not the saved row, so you can test an edit before
  // committing it. Needs a saved template to hang the send off, so the button is
  // hidden until the first save.
  const testMut = useMutation({
    mutationFn: (phone: string) =>
      marketingSvc.sendTestSmsTemplate(templateId as number, phone, { messageBody, mediaUrls }),
    onSuccess: () => { toast.success('Test SMS sent!'); setShowTestModal(false); setTestPhoneDigits(''); },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message || 'Test send failed';
      toast.error(msg);
    },
  });

  // Every save (and activation) carries opt-out instructions: the token is
  // appended when the message has none, the way the email editors add the
  // unsubscribe footer. Returns the body to save; the toast is the only sign
  // anything was added.
  const withOptOut = useCallback((): string => {
    const body = ensureSmsOptOut(messageBody);
    if (body !== messageBody) {
      setMessageBody(body);
      toast.info('Opt-out instructions added', `${SMS_OPT_OUT_TOKEN} was added to the end of the message.`);
    }
    return body;
  }, [messageBody, toast]);

  const handleSave = useCallback(
    (activeOverride?: boolean) => {
      if (!name.trim()) {
        toast.error('Template name is required');
        return;
      }
      saveMut.mutate({
        name: name.trim(),
        description,
        messageBody: withOptOut(),
        mediaUrls: JSON.stringify(mediaUrls),
        active: activeOverride !== undefined ? activeOverride : active,
        folder,
      });
      if (activeOverride !== undefined) setActive(activeOverride);
    },
    [name, description, withOptOut, mediaUrls, active, folder, saveMut, toast],
  );

  // ── Activation review (replicates PHP _gatherReview + showActivateModal) ──
  // Reviews the body that activation will save (see handleActiveToggle), which
  // is why it takes the body rather than reading state.
  const gatherActivationReview = useCallback((body: string): { items: ActivationReviewItem[]; hasErrors: boolean } => {
    const segs = getSmsSegments(body);
    const sr = segmentRating(segs);
    const items: ActivationReviewItem[] = [
      { label: 'Template Name', ok: !!name.trim(), value: name.trim() || null },
      { label: 'Description', ok: !!description.trim(), value: description.trim() || null, optional: true },
      { label: 'SMS Content', ok: hasSmsMessageContent(body) },
      { label: `Opt-out instructions (${SMS_OPT_OUT_TOKEN})`, ok: hasSmsOptOut(body) },
      {
        label: `Characters: ${body.length}`,
        ok: body.length > 0 && body.length <= 1600,
        detail: /[^\x00-\x7F]/.test(body) ? 'Unicode detected' : undefined,
        optional: true,
      },
      { label: `Segments: ${segs} — ${sr.label}`, ok: sr.cls !== 'fail', detail: sr.detail, optional: sr.cls === 'info' },
    ];

    if (mediaUrls.length > 0) {
      items.push({ label: `Media Files: ${mediaUrls.length} of ${MMS_MAX_FILES}`, ok: mediaUrls.length <= MMS_MAX_FILES });
    }

    const hasErrors = items.some((i) => !i.ok && !i.optional);
    return { items, hasErrors };
  }, [name, description, mediaUrls]);

  const handleActiveToggle = useCallback((checked: boolean) => {
    if (checked) {
      // Activation saves, and saving adds the opt-out token, so review the
      // message as it will be saved rather than failing it for what save fixes.
      const { items, hasErrors } = gatherActivationReview(withOptOut());
      setActivationReview(items);
      setActivationHasErrors(hasErrors);
      setShowActivateModal(true);
    } else {
      setActive(false);
      if (templateId) handleSave(false);
    }
  }, [gatherActivationReview, withOptOut, templateId, handleSave]);

  const confirmActivate = useCallback(() => {
    setShowActivateModal(false);
    setActive(true);
    handleSave(true);
  }, [handleSave]);

  const handleBack = useCallback(() => {
    navigate(SMS_TEMPLATES_LIST);
  }, [navigate]);

  // ── Loading ──
  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-1 rounded hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <MessageCircle className="h-4 w-4 text-emerald-600" />
          <span className="font-semibold text-slate-800">
            {isNew ? 'Create SMS Template' : `Edit Template: ${name || 'Untitled'}`}
          </span>
          {hasUnsavedChanges && (
            <div className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!!templateId && (
            <Button variant="ghost" size="sm" onClick={() => setShowTestModal(true)}>
              <Phone className="h-4 w-4 mr-1" /> Send Test
            </Button>
          )}
          <Button size="sm" loading={saveMut.isPending} onClick={() => handleSave()}>
            <Save className="h-4 w-4 mr-1" /> Save Template
          </Button>
        </div>
      </div>

      {/* Main layout: editor + phone preview */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Editor Panel */}
        <div className="flex-[3] overflow-y-auto">
          {/* Sticky header inside left panel */}
          <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">SMS Template Details</span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <span className={cn('text-sm font-medium', active ? 'text-green-600' : 'text-slate-400')}>
                    {active ? 'Active' : 'Inactive'}
                  </span>
                  <Switch
                    checked={active}
                    onCheckedChange={handleActiveToggle}
                  />
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xs text-slate-500">Est. Cost Per Contact:</span>
                  <span className="text-lg font-bold text-orange-500">${cost.total.toFixed(4)}</span>
                </div>
                <span className="text-[11px] text-slate-400">{cost.label}</span>
              </div>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Template Name */}
            <FormField label="Template Name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter template name"
              />
            </FormField>

            {/* Message Content label */}
            <label className="text-sm font-semibold text-slate-700">Message Content</label>

            {/* Shared SMS Editor */}
            <SmsEditor
              messageBody={messageBody}
              onMessageChange={setMessageBody}
              mediaUrls={mediaUrls}
              onMediaChange={setMediaUrls}
            />

            {/* Description */}
            <FormField label="Description" helpText="For internal reference only -- not visible to recipients">
              <Textarea autoResize
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Internal notes about this template..."
              />
            </FormField>

            {/* Folder */}
            <FormField label="Folder">
              <Select
                options={folderOptions}
                value={folder}
                onChange={(v) => setFolder(v ?? 'general')}
              />
            </FormField>
          </div>
        </div>

        {/* Phone Preview Panel */}
        <div className="flex-[2] bg-slate-100 flex flex-col items-center justify-start pt-6 overflow-y-auto">
          <div className="text-center mb-3 text-sm font-semibold text-slate-500">Preview</div>
          <SmsPhoneMockup
            messageBody={messageBody}
            mediaUrls={mediaUrls}
            headerText="Message Preview"
            appendOptOut
          />
        </div>
      </div>

      {/* Test SMS Modal */}
      <Modal isOpen={showTestModal} onClose={() => setShowTestModal(false)} title="Send Test SMS" size="sm">
        <div className="space-y-4">
          <FormField label="Phone Number" helpText="Enter the 10-digit phone number to receive the test message">
            <Input
              value={
                testPhoneDigits.length === 10
                  ? `(${testPhoneDigits.slice(0, 3)}) ${testPhoneDigits.slice(3, 6)}-${testPhoneDigits.slice(6)}`
                  : testPhoneDigits
              }
              onChange={(e) => setTestPhoneDigits(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="(555) 123-4567"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && testPhoneDigits.length === 10) {
                  e.preventDefault();
                  testMut.mutate(testPhoneDigits);
                }
              }}
            />
          </FormField>
          <p className="text-xs text-slate-400">
            The message on screen is what gets sent — unsaved edits included. Personalization
            variables fall back to their defaults.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowTestModal(false)}>Cancel</Button>
            <Button
              loading={testMut.isPending}
              disabled={testPhoneDigits.length !== 10 || !messageBody.trim()}
              onClick={() => testMut.mutate(testPhoneDigits)}
            >
              <Phone className="h-4 w-4 mr-1" /> Send Test
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={() => deleteMut.mutate()}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />

      {/* Activation Review Modal (replicates PHP builder.php showActivateModal) */}
      {showActivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="flex items-center gap-2">
                {activationHasErrors ? (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
                    <X className="h-4 w-4 text-red-600" />
                  </div>
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                )}
                <h3 className="text-lg font-semibold text-gray-800">
                  {activationHasErrors ? 'Cannot Activate Template' : 'Activate Template'}
                </h3>
              </div>
              <button onClick={() => setShowActivateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              <p className="mb-4 text-sm text-gray-600">
                {activationHasErrors
                  ? 'The following issues must be resolved before this template can be made active:'
                  : 'All required fields are complete. Review the details below and confirm activation.'}
              </p>

              {/* Template Details */}
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Template Details</div>
              <ul className="mb-4 space-y-1.5">
                {activationReview
                  .filter((r) => ['Template Name', 'Description'].includes(r.label))
                  .map((r) => (
                    <li key={r.label} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        {r.optional
                          ? <span className="h-4 w-4 text-center text-gray-400">—</span>
                          : r.ok
                            ? <Check className="h-4 w-4 text-green-500" />
                            : <X className="h-4 w-4 text-red-500" />}
                        <span className="text-gray-700">
                          {r.label}
                          {r.optional && <span className="ml-1 text-xs text-gray-400">(optional)</span>}
                        </span>
                      </div>
                      <span className={r.ok ? 'text-gray-500 truncate max-w-[200px]' : r.optional ? 'text-gray-400 text-xs' : 'text-red-500 italic text-xs'}>
                        {r.ok ? (r.value || 'Included') : (r.optional ? 'Not included' : 'Missing')}
                      </span>
                    </li>
                  ))}
              </ul>

              {/* Message Content */}
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Message Content</div>
              <ul className="mb-4 space-y-1.5">
                {activationReview
                  .filter((r) => r.label === 'SMS Content' || r.label.startsWith('Opt-out') || r.label.startsWith('Characters'))
                  .map((r) => (
                    <li key={r.label} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        {r.ok
                          ? <Check className="h-4 w-4 text-green-500" />
                          : <X className="h-4 w-4 text-red-500" />}
                        <span className="text-gray-700">{r.label}</span>
                      </div>
                      <span className={r.ok ? 'text-green-600 text-xs' : 'text-red-500 text-xs italic'}>
                        {r.ok ? (r.detail || 'Included') : 'Not included'}
                      </span>
                    </li>
                  ))}
              </ul>

              {/* Media (if any) */}
              {activationReview.some((r) => r.label.startsWith('Media')) && (
                <>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Media Attachments</div>
                  <ul className="mb-4 space-y-1.5">
                    {activationReview
                      .filter((r) => r.label.startsWith('Media'))
                      .map((r) => (
                        <li key={r.label} className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-sm">
                          <div className="flex items-center gap-2">
                            {r.ok
                              ? <Check className="h-4 w-4 text-green-500" />
                              : <X className="h-4 w-4 text-red-500" />}
                            <span className="text-gray-700">{r.label}</span>
                          </div>
                          <span className={r.ok ? 'text-green-600 text-xs' : 'text-red-500 text-xs italic'}>
                            {r.ok ? 'OK' : 'Over limit'}
                          </span>
                        </li>
                      ))}
                  </ul>
                </>
              )}

              {/* Segment Analysis */}
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Segment Analysis</div>
              <ul className="space-y-1.5">
                {activationReview
                  .filter((r) => r.label.startsWith('Segments'))
                  .map((r) => (
                    <li key={r.label} className="rounded-md border border-gray-100 px-3 py-2 text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {r.ok
                            ? <Check className="h-4 w-4 text-green-500" />
                            : <X className="h-4 w-4 text-red-500" />}
                          <span className="text-gray-700">{r.label}</span>
                        </div>
                      </div>
                      {r.detail && <p className="text-xs text-gray-500 mt-1 ml-6">{r.detail}</p>}
                    </li>
                  ))}
              </ul>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
              {activationHasErrors ? (
                <button
                  onClick={() => setShowActivateModal(false)}
                  className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  OK
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setShowActivateModal(false)}
                    className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmActivate}
                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                  >
                    Activate &amp; Save
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
